import React, { useState, useMemo } from 'react';
import { Table2, BarChart3, PieChart, TrendingUp, AreaChart, Download, Maximize2 } from 'lucide-react';
import TableDisplay from './TableDisplay';
import ChartDisplay from './ChartDisplay';
import ChartModal from './ChartModal';
import { Column, Row, ChartType } from './types';
import { parseTableData } from './dataUtils';

interface DataVisualizationProps {
  columns: Column[];
  rows: Row[];
}

const chartOptions: { type: ChartType; icon: React.ReactNode; label: string }[] = [
  { type: 'bar', icon: <BarChart3 className="w-4 h-4" />, label: 'Barras' },
  { type: 'pie', icon: <PieChart className="w-4 h-4" />, label: 'Pizza' },
  { type: 'line', icon: <TrendingUp className="w-4 h-4" />, label: 'Linha' },
  { type: 'area', icon: <AreaChart className="w-4 h-4" />, label: 'Area' },
];

const DataVisualization: React.FC<DataVisualizationProps> = ({ columns, rows }) => {
  const parsedData = useMemo(() => parseTableData(columns, rows), [columns, rows]);
  const [view, setView] = useState<'table' | 'chart'>('table');
  const [chartType, setChartType] = useState<ChartType>(parsedData.suggestedChart);
  const [modalOpen, setModalOpen] = useState(false);

  const handleExportCSV = () => {
    const headers = columns.map(c => c.name).join(',');
    const csvRows = rows.map(row =>
      row.values.map(v => `"${(v.str || '').replace(/"/g, '""')}"`).join(',')
    );
    const csv = [headers, ...csvRows].join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `dados-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <div className="space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
            <button
              onClick={() => setView('table')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                view === 'table'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Table2 className="w-3.5 h-3.5" />
              Tabela
            </button>
            {parsedData.canChart && (
              <button
                onClick={() => setView('chart')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                  view === 'chart'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <BarChart3 className="w-3.5 h-3.5" />
                Grafico
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            {view === 'chart' && parsedData.canChart && (
              <>
                <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
                  {chartOptions.map(opt => (
                    <button
                      key={opt.type}
                      onClick={() => setChartType(opt.type)}
                      className={`flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all ${
                        chartType === opt.type
                          ? 'bg-blue-600 text-white shadow-sm'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                      title={opt.label}
                    >
                      {opt.icon}
                      <span className="hidden sm:inline">{opt.label}</span>
                    </button>
                  ))}
                </div>

                <button
                  onClick={() => setModalOpen(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-100 transition-colors border border-gray-200"
                  title="Expandir grafico"
                >
                  <Maximize2 className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Expandir</span>
                </button>
              </>
            )}

            <button
              onClick={handleExportCSV}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-100 transition-colors border border-gray-200"
              title="Exportar CSV"
            >
              <Download className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">CSV</span>
            </button>
          </div>
        </div>

        <div className="flex items-center gap-3 text-xs text-gray-500">
          <span className="bg-gray-100 px-2 py-1 rounded-md">
            {rows.length} registro{rows.length !== 1 ? 's' : ''}
          </span>
          <span className="bg-gray-100 px-2 py-1 rounded-md">
            {columns.length} coluna{columns.length !== 1 ? 's' : ''}
          </span>
        </div>

        {view === 'table' ? (
          <TableDisplay columns={columns} rows={rows} />
        ) : (
          parsedData.canChart && (
            <div
              onClick={() => setModalOpen(true)}
              className="cursor-pointer group relative"
              title="Clique para expandir"
            >
              <div className="absolute top-3 right-3 z-10 opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 backdrop-blur-sm rounded-lg px-3 py-1.5 shadow-sm border border-gray-200 flex items-center gap-1.5 text-xs text-gray-600 pointer-events-none">
                <Maximize2 className="w-3.5 h-3.5" />
                Clique para expandir
              </div>
              <div className="group-hover:ring-2 group-hover:ring-blue-200 rounded-lg transition-all">
                <ChartDisplay
                  data={parsedData.chartData}
                  chartType={chartType}
                  valueKeys={parsedData.valueKeys}
                  labelKey={parsedData.labelKey}
                />
              </div>
            </div>
          )
        )}
      </div>

      {parsedData.canChart && (
        <ChartModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          data={parsedData.chartData}
          initialChartType={chartType}
          valueKeys={parsedData.valueKeys}
          labelKey={parsedData.labelKey}
        />
      )}
    </>
  );
};

export default DataVisualization;
